/**
 * Which requestor keys an order save should carry (#543).
 *
 * PATCH /receiving/:id is a genuine partial update: a key the payload leaves
 * out is left alone, and an explicit null clears the stored value. So the
 * drawer has to choose between three payloads rather than always sending what
 * the picker happens to show.
 *
 * An untouched picker must send NOTHING. It is not enough that the server
 * stopped nulling unsent columns — the picker has to leave the requestor out
 * of a save that did not touch it, or it re-creates the reported defect one
 * layer above the fix.
 *
 * Clearing the picker deliberately sends BOTH halves as null: the display
 * snapshot is denormalized from the link, and a name left behind by a cleared
 * link would keep showing in the list as though the order still had a
 * requestor.
 */
export interface RequestorPatch {
  requestorId?: string | null;
  requestorName?: string | null;
}

/**
 * The picker value for "keep the external requestor this order already has".
 *
 * An order imported from the legacy system can carry a requestor NAME with no
 * user record behind it. The first version of this picker had no option for
 * one, so it sat on "none" — and "none because untouched" and "none because
 * the user chose it" were then the same value, which made the external name
 * impossible to clear from the drawer (review of #544). Giving it its own
 * option means every choice the user can make lands on a distinct value, so
 * intent can be read from the final selection alone.
 *
 * Never a real user id, and never sent to the API.
 */
export const KEEP_EXTERNAL_REQUESTOR = '__external__';

/** The value the picker opens on for an order. */
export function initialRequestorValue(order: {
  requestorId: string | null;
  requestorName: string | null;
}): string {
  if (order.requestorId) return order.requestorId;
  return order.requestorName?.trim() ? KEEP_EXTERNAL_REQUESTOR : '';
}

/**
 * @param selected the picker's current value — a user id, `''` for none, or
 *                 KEEP_EXTERNAL_REQUESTOR
 * @param initial  what the drawer opened with — `initialRequestorValue(order)`
 */
export function requestorPatch(selected: string, initial: string): RequestorPatch {
  if (selected === initial) return {};
  // Only offered when it IS the initial value, so this cannot normally be
  // reached — but the sentinel must never reach the API as an id, whatever
  // path got it here.
  if (selected === KEEP_EXTERNAL_REQUESTOR) return {};
  if (selected === '') return { requestorId: null, requestorName: null };
  // The display name is resolved server-side from the id, exactly as it is on
  // create, so the client never has to keep the two in step itself.
  return { requestorId: selected };
}
